import _, { indexOf, values } from 'lodash';
import { createSelector } from 'reselect';

import {
  get,
  post,
  put,
  del,
} from 'utils/fetch';

import { RequestState } from 'constants/constants';
import { listReducer, listWithout, createListSelector, createList } from 'utils/list';

import { selectCurrentAccountId } from 'store/models/Account';
import { FETCH_CHANNEL_LINK_STATS, FETCH_CAMPAIGN_LINK_STATS } from 'store/models/Stats';



// ======================
// HELPERS
// ====================== 




// ======================
// ACTIONS
// ====================== 
export const FETCH_LINKS = 'FETCH_LINKS';
export const FETCH_LINKS_DELETED = 'FETCH_LINKS_DELETED';
export const FETCH_LINKS_CHANNEL = 'FETCH_LINKS_CHANNEL';
export const FETCH_CAMPAIGN_LINK_URLS = 'FETCH_CAMPAIGN_LINK_URLS';
export const FETCH_CHANNEL_LINK_URLS = 'FETCH_CHANNEL_LINK_URLS';
export const CREATE_LINK = 'CREATE_LINK';
export const UPDATE_LINK = 'UPDATE_LINK';
export const RESTORE_LINK = 'RESTORE_LINK';
export const DELETE_LINK = 'DELETE_LINK';


// ======================
// SELECTORS
// ====================== 
export const selectAllLinks = state => state.models.links;
export const selectLink = (state, linkId) => state.models.links[linkId];
export const selectLinks = (state, accountId) => selectLinksByAccount(state, accountId ?? selectCurrentAccountId(state));
export const selectDeletedLinks = (state, accountId) => state.models.linksDeleted;
export const selectLinkUrls = (state, campaignId, linkId) => state.models.linkUrls[campaignId]?.[linkId];
export const selectChannelLinkUrls = (state, channelId) => state.models.linkUrls[channelId];
export const selectLinksByAccount = createSelector(
  selectAllLinks,
  (state, accountId) => accountId ?? selectCurrentAccountId(state),
  (links, accountId) => _.filter(links, link => link.accountId === accountId)
);
export const selectLinksByIds = createSelector(
  selectAllLinks,
  (_, linkIds) => linkIds,
  (links, linkIds) => _.pick(links, linkIds)
);
export const selectLinksForChannel = createListSelector('linksForChannel');
export const selectLinksForCampaign = createListSelector('linksForCampaign');
  




// ======================
// MODEL
// ====================== 
const Link = {
  
  actions: {
    fetchLinks: accountId => (dispatch, getState) => {
      accountId = accountId ?? selectCurrentAccountId(getState());
      return get({
        url: '/api/account/:accountId/links',
        params: { accountId },
        dispatch,
        action: FETCH_LINKS,
      })
    },

    fetchDeletedLinks: accountId => (dispatch, getState) => {
      accountId = accountId ?? selectCurrentAccountId(getState());
      return get({
        url: '/api/account/:accountId/links/deleted',
        params: { accountId },
        dispatch,
        action: FETCH_LINKS_DELETED,
      })
    },

    fetchLink: linkId => dispatch => get({
      url: '/api/link/:linkId',
      params: { linkId },
      dispatch,
      action: FETCH_LINKS,
    }),

    fetchLinksForChannel: (channelId, from, to) => dispatch => get({
      url: '/api/links/channel/:channelId',
      params: { channelId, from, to },
      dispatch,
      action: FETCH_LINKS_CHANNEL,
    }),

    fetchLinkUrls: (campaignId, linkId) => dispatch => get({
      url: '/api/campaign/:campaignId/link/:linkId/urls',
      params: { campaignId, linkId },
      dispatch,
      action: FETCH_CAMPAIGN_LINK_URLS,
    }),

    fetchChannelLinkUrls: (channelId, accountId) => (dispatch, getState) => 
      accountId ?? selectCurrentAccountId(getState())
        |> get({
            url: '/api/account/:accountId/channel/:channelId/link_urls',
            params: { accountId: #, channelId },
            dispatch,
            action: FETCH_CHANNEL_LINK_URLS,
           }),

    createLink: values => dispatch => post({
      url: '/api/link',
      body: values,
      dispatch,
      action: CREATE_LINK,
    }),

    updateLink: (linkId, values) => dispatch => put({
      url: '/api/link/:linkId',
      params: { linkId },
      body: values,
      dispatch,
      action: UPDATE_LINK,
    }),

    restoreLink: (linkId) => dispatch => put({
      url: '/api/link/:linkId/restore',
      params: { linkId },
      body: {},
      dispatch,
      action: RESTORE_LINK,
    }),

    deleteLink: linkId => dispatch => del({
      url: '/api/link/:linkId',
      params: { linkId },
      dispatch,
      action: DELETE_LINK,
    }),



  },

  spec: {
    links: {},
    linksDeleted: {},
    linkUrls: {}
  },

  reducer: (state, type, body, action, json) => {
    if(action.url && action.result !== RequestState.SUCCESS)
      return state;

    if(body && body.links) {
      state = { ...state, 
        links: { ...state.links, 
          ..._.keyBy(body.links, '_id'), 
        }
      };
    }

    switch(type) {
      case FETCH_CAMPAIGN_LINK_URLS:
        return { ...state,
          linkUrls: { ...state.linkUrls,
            [action.params.campaignId]: {
              ...state.linkUrls[action.params.campaignId],
              [action.params.linkId]: body.linkUrls,
            }
          }
        }
      case FETCH_CHANNEL_LINK_URLS:
        return { ...state,
          linkUrls: {
            ...state.linkUrls,
            [action.params.channelId]: {
              ...body.linkUrls,
            }
          }
        }
      
      case FETCH_LINKS_DELETED:
        return { ...state,
          linksDeleted: { ...state.linksDeleted,
            ..._.keyBy(body.linksDeleted, '_id'), 
          }
        }
      
      case RESTORE_LINK: {
        return {
          ...state,
          linksDeleted:_.omit(state.linksDeleted, action.params.linkId),
        }
      } 

      case DELETE_LINK: {
        let links = _.omit(state.links, action.params.linkId);
        return {...state,
          links,
          linksDeleted: { ...state.linksDeleted,
            [action.params.linkId]: state.links[action.params.linkId],
          }
        }
      }
        
    }
    return state;
  },

  listSpec: {
    linksByAccount: {},
    linksForChannel: {},
    linksForCampaign: {},
  },

  listReducer: (state, type, body, action, json) => {
    if(action.url && action.result !== RequestState.SUCCESS)
      return state;

    if(body && body.links) {
      state = { ...state,
        linksByAccount: listReducer(state, 'linksByAccount', body.links, 'accountId', 'links', '_id'),
      };
    }

    switch(type) {
      case DELETE_LINK:
        return { ...state,
          linksByAccount: listWithout(state, 'linksByAccount', action.params.linkId),
        }
      case FETCH_LINKS_CHANNEL:
      case FETCH_CHANNEL_LINK_STATS:
        return { ...state,
          linksForChannel: {
            ...state.linksForChannel,
            [action.params.channelId]: createList(body.links, 'links', '_id')
          } 
        }
      case FETCH_CAMPAIGN_LINK_STATS:
        return { ...state,
          linksForCampaign: {
            ...state.linksForCampaign,
            [action.params.campaignId]: createList(body.links, 'links', '_id')
          } 
        }
    }

    return state;
  },
}
export default Link;